import i18n from '@/utils/i18n';
import React, { useCallback } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { getOrderSourceContact, getSubjectTranslation } from './utils';
import {
  OrderInboundsAssignedEmployee,
  UnassignedEmployeeTrigger,
} from './order-inbounds-assigned-employee';
import { OrderMetadata } from './types';
import { Text, View } from 'react-native';
import { TYPOGRAPHY } from '@/styles';
import { useLayoutContext } from '@/contexts/layout-context';
import { useToasts } from '@/contexts/toast-context';
import { useUpdateAssignedEmployee } from './queries';

interface Props {
  hideHeaderSubject?: boolean;
  isFirstOrder?: boolean;
  isSummaryView?: boolean;
  order: OrderMetadata;
  showHeaderMetadata?: boolean;
  showTranslation?: boolean;
}

export const OriginalInboundMessageHeader: React.FC<Props> = ({
  hideHeaderSubject,
  isSummaryView,
  order,
  showTranslation,
  showHeaderMetadata = true,
  isFirstOrder = false,
}) => {
  const [styles] = useStyles();
  const { isMediumLayout, isLargeLayout } = useLayoutContext();
  const { showErrorToast } = useToasts();
  const updateAssignedEmployee = useUpdateAssignedEmployee();

  // TODO: Note on `isSummaryView`:
  // we should probably look to refactor the conditional rendering around whether or not we are in the "summary" view or
  // not. these additional conditionals combinatorially explodes the number of states this component can be in,
  // as well as lead to more confusing conditional variables down the line (isFullContentLargeLayout is already not
  // that straightforward, especially finding whether or not the prop is actually passed in or if the parent forgot to
  // provide it, which lead to to bug EXP-3344).
  //
  // it would probably be better to allow the parent component decide how the header metadata should be constructed (by
  // allow a prop like `headerMetadataSection?: ReactNode` or something similar) or break this component down so that
  // all parts of it can be constructed by the parent itself. this way all we have to do in this component is to render
  // their constructed header metadata component
  //
  // the biggest advantage of this would be a much more composable component that is fully customizable, but with the
  // added overhead of coming up with the right way to do this.
  const isFullContentLargeLayout = !isSummaryView && isLargeLayout;

  const onUpdateAssignedEmployeeActionClick = useCallback(
    async (employeeUUID: string | null) => {
      try {
        await updateAssignedEmployee({
          employeeUUID,
          smartOrderUUIDs: [order.smartOrderUUID],
          supplierUUID: order.supplierUUID,
        });
      } catch (err) {
        showErrorToast();
      }
    },
    [
      order.smartOrderUUID,
      order.supplierUUID,
      showErrorToast,
      updateAssignedEmployee,
    ],
  );

  const isEmail = order.subject || order.source === 'EMAIL';
  const sourceContact = getOrderSourceContact(order);

  const deliveryText = isFullContentLargeLayout ? null : order.deliveryText;
  const subjectTranslation = showTranslation
    ? getSubjectTranslation(order)
    : null;
  const subject = hideHeaderSubject
    ? null
    : (subjectTranslation ?? order.subject);
  const subtitleParts = [order.fullDate, deliveryText].filter(Boolean);

  // If we have no part of the content, we don't need to render the header
  const hasNoMetadata = isFullContentLargeLayout && subtitleParts.length === 0;
  const metadataIsHidden = !showHeaderMetadata || hasNoMetadata;
  if (metadataIsHidden && !subject) {
    return null;
  }

  const shouldDisplayAssignedEmployee =
    isMediumLayout && !isSummaryView && isFirstOrder;

  const headerMetadata = (
    <>
      {isFullContentLargeLayout ? null : (
        <View style={[styles.line, styles.lineContact]}>
          {sourceContact ? (
            <Text
              numberOfLines={1}
              style={[TYPOGRAPHY.h4SemiBold, styles.textContact]}
              testID={`original-inbound-line${order.employee?.name ? '-name' : ''}${isEmail ? '-email' : '-phone'}`}
            >
              {order.employee?.name
                ? `${order.employee.name} • ${sourceContact}`
                : sourceContact}
            </Text>
          ) : null}

          {shouldDisplayAssignedEmployee ? (
            <>
              <OrderInboundsAssignedEmployee
                assignedEmployee={order.assignedEmployee}
                onAssignEmployee={onUpdateAssignedEmployeeActionClick}
                onUnassignEmployee={() =>
                  void onUpdateAssignedEmployeeActionClick(null)
                }
                renderUnassignedTrigger={(onPress) => (
                  <UnassignedEmployeeTrigger onPress={onPress} />
                )}
                restaurantName={order.restaurantName}
                smartOrderUUID={order.smartOrderUUID}
                supplierUUID={order.supplierUUID}
              />
            </>
          ) : null}
        </View>
      )}
      {subtitleParts.length ? (
        <View style={[styles.line, styles.lineDates]}>
          <Text
            style={[TYPOGRAPHY.h4, styles.textDates]}
            testID={`original-inbound-line${deliveryText ? '-delivery' : ''}`}
          >
            {subtitleParts.join(' • ')}
          </Text>
        </View>
      ) : null}
    </>
  );

  return (
    <View style={styles.header}>
      {showHeaderMetadata ? headerMetadata : null}
      {subject ? (
        <View style={[styles.line, styles.lineSubject]}>
          <Text
            style={[TYPOGRAPHY.h4, styles.textSubject]}
            testID="original-inbound-line-subject"
          >
            {i18n.t('subject', { subject })}
          </Text>
        </View>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  header: {
    alignItems: 'center',
    flex: 1,
    gap: 8,
  },
  line: {
    flexDirection: 'row',
    width: '100%',
  },
  lineContact: {
    gap: 8,
    justifyContent: 'space-between',
  },
  lineDates: {
    justifyContent: 'flex-start',
  },
  lineSubject: {
    justifyContent: 'flex-start',
    marginTop: 8,
  },
  textContact: {
    color: theme.sk.color.text.primary,
    flexShrink: 1,
  },
  textDates: {
    color: theme.sk.color.text.secondary,
  },
  textSubject: {
    color: theme.sk.color.text.primary,
  },
}));
